<script setup lang="ts">
import { router } from './router'
import MJourneyResumeBar from '@/components/common/m-journey-resume-bar.vue'
import type { AerialTimerRecord } from '@/utils/journeyResume'
import { PageName, StorageKey } from './models/enums'
import { UserTokens } from './models/login'
import mLoading from '@/components/common/m-loading.vue'
import MBookingProgress from '@/components/common/m-booking-progress.vue'
import { useTimeout } from '@/hooks/useTimer'
import { usePageSpy } from '@/hooks/usePageSpy'
import { UserOrdersResponseV3 } from './models/order'
const gStore = useGlobalStore()
const journeyStore = useJourneyStore()
const spotStore = useSpotStore()
const userStore = useUserStore()
const { timer: getRunningJourneyTimer, clearTimer: clearGetRunningJourneyTimer } =
  useTimeout('interval')
const pageSpy = usePageSpy()
// 进入这个 webview 时的原始地址。iOS 微信的 JS-SDK 签名认的是它 ——
// 下面 stripTokenFromUrl 会改掉地址栏，所以必须先抓住。
const entryHref = window.location.href

/**
 * 把 URL 上的登录凭证抹掉。
 *
 * 小程序跳过来时会把整份 userInfo（含完整可用的 access_token）塞在 query 里。
 * setUserInfoByUrl 消费完就存进 store 了，但它原来会一直留在地址栏上，于是：
 *   · 用户长按「复制链接」分享出去 ＝ 把账号发出去（token 有效期约 7 天）
 *   · 页面存活期间所有发往外域的请求（高德 / OSS / 字体），Referer 里都带着它
 *   · router 里三处 `redirect=${encodeURIComponent(window.location.href)}`
 *     会把它原样传回小程序的路由参数
 *
 * ⚠️ 必须**跑两次**，少一次都不行（实测，别精简）：
 *   ① setup 阶段同步跑 —— router 的首次导航守卫、以及上面那三处 redirect
 *      都比 onMounted 早，只在 onMounted 清就已经漏出去了；
 *   ② router.isReady() 之后再跑一次 —— Vue Router 首次导航结束时会用它
 *      自己的 fullPath 再 replaceState 一次，把 ① 的清理原样覆盖回去
 *      （实测：只做 ① 的话地址栏上 userInfo 还在）。
 *
 * 不走 router.replace 清 query，是因为那会让守卫重跑一遍 —— 小程序里
 * 「访问登录/设置/个人页」那三条守卫会再触发一次 uni.navigateTo，
 * 代价比收益大。这里只改地址栏：Referer、复制链接、window.location.href
 * 那三条泄漏路径看的都是它。
 *
 * 这只是缓解，不是根治 —— 小程序那头、以及首个请求的 Referer 仍然见过它。
 * 根治要小程序改传一次性短码、后端加换码接口（另开单跟）。
 */
const stripTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('userInfo')) return
    url.searchParams.delete('userInfo')
    window.history.replaceState(window.history.state, '', url.toString())
  } catch (err) {
    // 抹不掉也不能影响启动
    console.warn('清理 URL 上的登录凭证失败', err)
  }
}

const setUserInfoByUrl = () => {
  const userInfoStr = new URL(window.location.href).searchParams.get('userInfo')
  if (!userInfoStr) return
  // 无论解析成不成功，这一份都不该继续留在地址栏上
  stripTokenFromUrl()

  try {
    const userInfo = JSON.parse(userInfoStr) as UserTokens
    userStore.saveUserTokens(userInfo)
    userStore.setUserInformation({
      nickname: userInfo.nickname,
      avatar: userInfo.avatar,
      phone_number: userInfo.phone_number,
      gender: userInfo.gender,
      signature: userInfo.signature,
    })
    userStore.storageUserTokens()
    // ⚠️ 不要打印 userInfo 本身：它整个就是一份可用的登录凭证
    //（access_token + client_id + openid）。原来这行把它完整写进 console，
    // 于是 PageSpy 的日志里每一条都带着一个能直接用的账号。
    console.info('解析 url 上用戶信息成功', { userId: userInfo.userId })
  } catch (err) {
    // 同理，失敗時也不要把原始串打出來 —— 它可能是个格式稍有问题、但
    // token 字段完好的串。只说长度，够定位问题了。
    console.info('解析 url 上用戶信息失敗，長度', userInfoStr.length)
  }
}

/**
 * TODO 这里要改造到 router.ts 中按照路由配置处理
 * 目前不需要登录的页面在未登录时也会被这里处理跳转到首页
 * 这里获取不到首次进入时的路由名称
 */
const checkForValidLogin = () => {
  const code = new URL(window.location.href).searchParams.get('code')
  const state = new URL(window.location.href).searchParams.get('state')

  // state 为 direct_login 时，不做操作
  if (state === 'direct_login' && code) return

  if (state) {
    const hotspot = state.split('#')[0]
    spotStore.setScanRecord(hotspot)
  }
  if (userStore.checkTokenIsExpired()) {
    // 这里小程序未登錄時，不跳转到小程序登出页（否则体验不好）
    if (!gStore.inMP) {
      // userStore.logout()
    }

    if (code) {
      userStore.userInfo.wechatCode = code
    }

    // if (gStore.inMP) {
    //   window.uni.navigateTo({
    //     url: `/pages/login/index?redirect=${encodeURIComponent(window.location.href)}`,
    //   })
    // } else {
    //   router.push({ name: PageName.LOGIN })
    // }
  }
}

// 起飞时 aerial 页写在 localStorage 里的那条记录的键。
// ⚠️ 和 views/aerial/index.vue 的 TIMER_STORAGE_KEY 是同一个，改一处要改两处。
// 没有抽成常量是因为那边是页面内的裸字符串；这里只读不写，先对齐值。
const AERIAL_TIMER_KEY = 'aerial_timer_data'

// 「航拍还在飞」的接续条状态。
//
// ⚠️ 只有一个状态，这是 2026-09-16 实机之后刻意收窄的：第一版按 orderStatus
// 分了「还在飞 / 已结束 / 已中断」三档，结果航拍明明正在进行、条上却写着
// 「上次航拍已结束」—— 那套状态映射是猜的。现在只认
// findliveStreamPushedByTicketId 命中，那是原来硬跳用的同一个判据，
// 被生产验证过。没命中就什么都不显示。
const resume = reactive({
  visible: false,
  elapsedText: '',
  ticketId: '',
  hotspotId: '',
})

const onResumeGo = () => {
  const { ticketId, hotspotId } = resume
  router.replace({
    name: PageName.AERIAL,
    params: { journey_id: ticketId, hotspot: hotspotId, is_started: 1 },
  })
}

const onResumeDismiss = () => {
  resume.visible = false
  gStore.journeyResumeVisible = false
}

const runningJourneyTick = async () => {
  if (document.hidden) return
  if (!userStore.checkIsLogin) return

  const currentRoute = router.currentRoute.value
  const routeName = currentRoute.name as PageName
  if (![PageName.HOME, PageName.COMMUNITY, PageName.PROFILE].includes(routeName)) return

  const [err, res] = await to(queryRunningJourneyV2())
  if (err) {
    journeyStore.runningJourney = {
      ticketId: '',
      hotspotId: '',
    } as UserOrdersResponseV3
    return
  }
  const { ticketId, hotspotId } = res
  if (ticketId) {
    journeyStore.runningJourney = res
    // 不再硬跳。先浮一条接续条，用户撳了（或倒计时走完）才过去 ——
    // 原来是 router.replace 直接换屏，零过渡零解释，用户的心理模型是
    // 「我被踢走了」而不是「我被接回去了」。
    if (!resume.visible) {
      resume.ticketId = ticketId
      resume.hotspotId = hotspotId
      const record = storage.getItem(AERIAL_TIMER_KEY) as AerialTimerRecord | null
      resume.elapsedText = isUsableRecord(record) ? formatElapsed(Date.now() - record.timestamp) : ''
      resume.visible = true
      // 让首页那颗「继续航拍」收起来，同一件事不说两遍
      gStore.journeyResumeVisible = true
    }
  }
}

const getRunningJourney = async () => {
  // ⚠️ 先立即跑一次，再进轮询。
  // setInterval **不会立即执行第一次** —— 原来重进小程序之后，用户会先看到
  // 一个完全正常的首页、浏览好几秒，才被突然拽走。那 6 秒不是设计，是
  // setInterval 的语义造成的盲区。
  runningJourneyTick()
  getRunningJourneyTimer.value = setInterval(runningJourneyTick, 6000)
}

setUserInfoByUrl()
// 见 stripTokenFromUrl 的注释 ②：router 首次导航会把地址栏改回去，
// 等它跑完再清一次。
router.isReady().then(stripTokenFromUrl)

onMounted(async () => {
  pageSpy.init()
  checkForValidLogin()

  // 用于jssdk url签名参数。
  // ⚠️ 用 entryHref 而不是当前 href：登录凭证已经在 setup 阶段被
  // stripTokenFromUrl 抹掉了，而 iOS 微信的签名认的是 webview 打开时那个
  // 原始地址，replaceState 改不了它的判断。
  storage.setItem(StorageKey.WX_CODE_URL, entryHref)
  getRunningJourney()
  console.log('platformInfo', JSON.stringify(gStore.platformInfo))
  await gStore.getAppShowState()
})
onUnmounted(() => {
  clearGetRunningJourneyTimer()
  pageSpy.stopUploadLog()
})
</script>

<template>
  <div
    :class="[
      'app-page',
      gStore.platformInfo.shell,
      gStore.platformInfo.system,
      gStore.platformInfo.systemVs,
    ]"
  >
    <!--
      两个遮罩，**开关是同一个** —— 都由 gStore.appLoad 控制，booking 只决定
      长成哪一个。所以下单进度这套即使整个坏掉，最坏结果是退回原来的转圈，
      遮罩该开该关的时机一点没变（useReservation 里那几处 setAppLoad 一个字没动）。

      booking.active 的清理挂在 setAppLoad(false) 里，不可能比遮罩活得久。
    -->
    <m-booking-progress
      v-if="!!gStore.appLoad.loading && gStore.booking.active"
      :step="gStore.booking.step"
      :summary="gStore.booking.summary"
    />
    <m-loading
      v-else
      :loading="!!gStore.appLoad.loading"
      :timeout="gStore.appLoad.timeout"
      :load-text="typeof gStore.appLoad.loading === 'string' ? gStore.appLoad.loading : ''"
    />
    <!-- 「航拍还在飞」的接续条。替掉原来的 router.replace 硬跳 ——
         先说清楚发生了什么，4 秒后（或撳一下）再回航拍页。 -->
    <m-journey-resume-bar
      v-if="resume.visible"
      :elapsed-text="resume.elapsedText"
      @go="onResumeGo"
      @dismiss="onResumeDismiss"
    />

    <RouterView></RouterView>
  </div>
</template>

<style scoped lang="scss">
@forward '@/style.scss';

.app-page {
  height: 100vh;
  height: 100dvh;
}

:deep(.amap-logo) {
  display: none !important;
}

:deep(.amap-copyright) {
  display: none !important;
}
</style>
